import React from 'react';
import {
    Container, Grid, Card, CardContent, Typography, Box, 
    Snackbar, Alert, useTheme, useMediaQuery, IconButton,
    Fab, AppBar, Toolbar, Menu, MenuItem
} from '@mui/material';
import {
    Description, ToggleOn, Business, People,
    Add, Search, FilterList,
    Refresh, Logout, Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import useAdminUsers from '../hooks/admin/useAdminUsers';
import AdminFilters from '../components/admin/AdminFilters';
import UsersTable from '../components/admin/UsersTable';
import TablePaginationBar from '../components/admin/TablePaginationBar';
import CreateUserDialog from '../components/admin/dialogs/CreateUserDialog';
import EditUserDialog from '../components/admin/dialogs/EditUserDialog';
import ResetPasswordDialog from '../components/admin/dialogs/ResetPasswordDialog';
import DeleteUserDialog from '../components/admin/dialogs/DeleteUserDialog';
import axios from 'axios';

const ADMIN_API = '/api/admin';
const routes = {
    dashboard: () => `${ADMIN_API}/dashboard`,
    users:     () => `${ADMIN_API}/users`,
    user:      (id) => `${ADMIN_API}/users/${id}`,
    toggle:    (id) => `${ADMIN_API}/users/${id}/toggle`,
    resetPwd:  (id) => `${ADMIN_API}/users/${id}/reset-password`,
};

export default function AdminDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const { user, logout } = useAuth();
    const { state, setQuery, load, pageCount, toggleUserOptimistic, withAuth } = useAdminUsers();
    const [stats, setStats] = React.useState({});
    const [snack, setSnack] = React.useState({ open: false, msg: '', sev: 'success' });
    
    // État pour les menus mobiles
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);

    // dialogs
    const [openCreate, setOpenCreate] = React.useState(false);
    const [editUser, setEditUser]     = React.useState(null);
    const [resetUser, setResetUser]   = React.useState(null);
    const [deleteUser, setDeleteUser] = React.useState(null);

    const notify = (msg, sev='success') =>
        setSnack({ open: true, msg, sev });

    // stats on mount
    React.useEffect(() => {
        (async () => {
            try {
                const { data } = await withAuth(() => axios.get(routes.dashboard()));
                setStats(data || {});
            } catch (e) {
                notify(e?.response?.data?.error || e.message || 'Erreur chargement statistiques', 'error');
            }
        })();
    }, []); // eslint-disable-line

    const refreshAll = React.useCallback(async () => {
        try {
            const { data } = await withAuth(() => axios.get(routes.dashboard()));
            setStats(data || {});
        } finally {
            load();
        }
    }, [load, withAuth]);

    const handleCreate = async (payload) => {
        try {
            await withAuth(() => axios.post(routes.users(), payload));
            setOpenCreate(false);
            setQuery({ page: 1 });
            await refreshAll();
            notify('Utilisateur créé');
        } catch (e) {
            notify(e?.response?.data?.error || 'Erreur création utilisateur', 'error');
        }
    };

    const handleEdit = async (u) => {
        try {
          const { _id, email, nom, prenom, role, password } = u;
          const payload = { email, nom, prenom, role };
          
          if (password) {
            payload.password = password;
          }
          
          await withAuth(() => axios.patch(routes.user(_id), payload));
          setEditUser(null);
          await refreshAll();
          notify('Utilisateur mis à jour');
        } catch (e) {
          notify(e?.response?.data?.error || 'Erreur sauvegarde utilisateur', 'error');
        }
      };

    const handleToggle = async (u) => {
        try {
            await toggleUserOptimistic(u._id, async () => {
                await withAuth(() => axios.patch(routes.toggle(u._id)));
            });
            await refreshAll();
            notify('Statut modifié');
        } catch (e) {
            notify(e?.response?.data?.error || 'Erreur changement statut', 'error');
        }
    };

    const handleResetPwd = async (u, pwd) => {
        try {
            await withAuth(() => axios.post(routes.resetPwd(u._id), { password: pwd }));
            setResetUser(null);
            notify('Mot de passe réinitialisé');
        } catch (e) {
            notify(e?.response?.data?.error || 'Erreur réinit. mot de passe', 'error');
        }
    };

    const handleDelete = async (u) => {
        try {
            await withAuth(() => axios.delete(routes.user(u._id)));
            setDeleteUser(null);
            const nextPage = (state.total - 1) <= (state.page - 1) * state.limit && state.page > 1
                ? state.page - 1 : state.page;
            setQuery({ page: nextPage });
            await refreshAll();
            notify('Utilisateur supprimé');
        } catch (e) {
            notify(e?.response?.data?.error || 'Erreur suppression utilisateur', 'error');
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterMenuOpen = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setFilterAnchorEl(null);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* AppBar simplifiée pour mobile */}
            {isMobile ? (
                <AppBar position="sticky">
                    <Toolbar>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            Administration
                        </Typography>
                        <IconButton color="inherit" onClick={refreshAll}>
                            <Refresh />
                        </IconButton>
                        <IconButton color="inherit" onClick={handleFilterMenuOpen}>
                            <FilterList />
                        </IconButton>
                        <IconButton color="inherit" onClick={handleMenuOpen}>
                            <MenuIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
            ) : (
                // En-tête pour desktop
                <Box sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
                    <Container maxWidth="xl">
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            py: 2
                        }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                Tableau de bord Admin
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1">
                                    {user?.prenom} {user?.nom}
                                </Typography>
                                <IconButton onClick={logout} color="primary">
                                    <Logout />
                                </IconButton>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            )}

            <Container 
                maxWidth="xl" 
                sx={{ 
                    py: { xs: 2, sm: 3 },
                    px: { xs: 1, sm: 2, md: 3 }
                }}
            >
                {/* KPIs */}
                <Grid 
                    container 
                    spacing={{ xs: 1, sm: 2, md: 3 }} 
                    sx={{ mb: { xs: 2, sm: 3 } }}
                >
                    {[
                        { icon: <People color="primary" />, value: stats.users || 0, label: "Utilisateurs" },
                        { icon: <Business color="secondary" />, value: stats.clients || 0, label: "Clients" },
                        { icon: <Description color="info" />, value: stats.documents || 0, label: "Documents" },
                        { icon: <ToggleOn color="success" />, value: stats.activeUsers || 0, label: "Actifs" }
                    ].map((kpi, index) => (
                        <Grid item xs={6} sm={6} md={3} key={index}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    borderRadius: { xs: 1, sm: 2 },
                                    boxShadow: { xs: 1, sm: 2 }
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Box 
                                        display="flex" 
                                        alignItems="center"
                                        sx={{
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            textAlign: { xs: 'center', sm: 'left' }
                                        }}
                                    >
                                        <Box sx={{ 
                                            mr: { sm: 2 },
                                            mb: { xs: 1, sm: 0 }
                                        }}>
                                            {React.cloneElement(kpi.icon, {
                                                fontSize: isMobile ? "medium" : "large"
                                            })}
                                        </Box>
                                        <Box>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"}
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                {kpi.value}
                                            </Typography>
                                            <Typography 
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {kpi.label}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Filtres */}
                <Card sx={{ mb: 2, borderRadius: { xs: 1, sm: 2 } }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                        {isMobile ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                                    Gestion des utilisateurs
                                </Typography>
                                <IconButton 
                                    color="primary" 
                                    size="small"
                                    onClick={() => setOpenCreate(true)}
                                >
                                    <Add />
                                </IconButton>
                                <IconButton 
                                    color="default" 
                                    size="small"
                                    onClick={refreshAll}
                                >
                                    <Refresh />
                                </IconButton>
                            </Box>
                        ) : (
                            <AdminFilters
                                search={state.search}
                                role={state.role}
                                status={state.status}
                                onChange={setQuery}
                                onRefresh={refreshAll}
                                onCreate={() => setOpenCreate(true)}
                            />
                        )}
                    </CardContent>
                </Card>

                {/* Table */}
                <Card sx={{ borderRadius: { xs: 1, sm: 2 }, overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 0, sm: 1, md: 2 } }}>
                        <Box sx={{ 
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                                height: 6
                            }
                        }}>
                            <Box sx={{ minWidth: 650 }}>
                                <UsersTable
                                    rows={state.items}
                                    loading={state.loading}
                                    sortBy={state.sortBy}
                                    order={state.order}
                                    onSort={(f) => setQuery({
                                        sortBy: f,
                                        order: state.sortBy === f ? (state.order === 'asc' ? 'desc' : 'asc') : 'asc'
                                    })}
                                    onToggle={handleToggle}
                                    onEdit={(u) => setEditUser(u)}
                                    onReset={(u) => setResetUser(u)}
                                    onDelete={(u) => setDeleteUser(u)}
                                    onRowUpdated={() => {
                                        refreshAll();
                                        notify('Ligne mise à jour');
                                    }}
                                    isMobile={isMobile}
                                />
                            </Box>
                        </Box>
                        
                        {/* Pagination */}
                        <Box sx={{ mt: 2, px: { xs: 1, sm: 0 } }}>
                            <TablePaginationBar
                                total={state.total}
                                page={state.page}
                                pageCount={pageCount}
                                limit={state.limit}
                                onChange={setQuery}
                                isMobile={isMobile}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Container>

            {/* FAB pour mobile */}
            {isMobile && (
                <Fab
                    color="primary"
                    aria-label="add"
                    onClick={() => setOpenCreate(true)}
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 1000
                    }}
                >
                    <Add />
                </Fab>
            )}

            {/* Menus pour mobile */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    logout();
                    handleMenuClose();
                }}>
                    Se déconnecter
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    setQuery({ role: '' });
                    handleMenuClose();
                }}>
                    Tous les rôles
                </MenuItem>
                <MenuItem onClick={() => {
                    setQuery({ role: 'admin' });
                    handleMenuClose();
                }}>
                    Admins seulement
                </MenuItem>
                <MenuItem onClick={() => {
                    setQuery({ role: 'user' });
                    handleMenuClose();
                }}>
                    Utilisateurs seulement
                </MenuItem>
                <MenuItem onClick={() => {
                    setQuery({ status: 'active' });
                    handleMenuClose();
                }}>
                    Actifs seulement
                </MenuItem>
                <MenuItem onClick={() => {
                    setQuery({ status: 'inactive' });
                    handleMenuClose();
                }}>
                    Inactifs seulement
                </MenuItem>
            </Menu>

            {/* Dialogs */}
            <CreateUserDialog 
                open={openCreate} 
                onClose={() => setOpenCreate(false)} 
                onCreate={handleCreate}
                fullScreen={isMobile}
            />
            <EditUserDialog 
                open={!!editUser} 
                user={editUser} 
                onClose={() => setEditUser(null)} 
                onSave={handleEdit}
                fullScreen={isMobile}
            />
            <ResetPasswordDialog 
                open={!!resetUser} 
                user={resetUser} 
                onClose={() => setResetUser(null)} 
                onReset={handleResetPwd}
                fullScreen={isMobile}
            />
            <DeleteUserDialog 
                open={!!deleteUser} 
                user={deleteUser} 
                onClose={() => setDeleteUser(null)} 
                onDelete={handleDelete}
                fullScreen={isMobile}
            />

            {/* Snackbar */}
            <Snackbar 
                open={snack.open} 
                autoHideDuration={3000} 
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{
                    vertical: isMobile ? 'bottom' : 'top',
                    horizontal: 'center'
                }}
            >
                <Alert 
                    onClose={() => setSnack(s => ({ ...s, open: false }))} 
                    severity={snack.sev} 
                    sx={{ 
                        width: '100%',
                        maxWidth: isMobile ? '90vw' : '400px'
                    }}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}